import React from "react";

function signed(value, digits = 3) {
  const number = Number(value);
  if (!Number.isFinite(number)) return "—";
  if (number === 0) return "0";
  return `${number > 0 ? "+" : ""}${number.toFixed(digits).replace(/\.?0+$/, "")}`;
}

function kind(value) {
  const number = Number(value);
  if (!Number.isFinite(number) || number === 0) return "neutral";
  return number > 0 ? "up" : "down";
}

function Metric({ label, value, description, digits = 3 }) {
  if (value === null || value === undefined) return null;
  const metricKind = kind(value);

  return (
    <div className={`rr-delta-metric ${metricKind}`}>
      <span>{metricKind === "up" ? "↑" : metricKind === "down" ? "↓" : "↔"}</span>
      <div>
        <b>{label}</b>
        <strong>{signed(value, digits)}</strong>
        <small>{description}</small>
      </div>
    </div>
  );
}

function summaryFragment(value, label, digits = 0) {
  const number = Number(value);
  if (!Number.isFinite(number) || number === 0) return null;
  const amount = Math.abs(number).toFixed(digits).replace(/\.?0+$/, "");
  return `${amount} ${number > 0 ? "more" : "fewer"} ${label}`;
}

export default function DeltaInspector({
  metrics,
  rootStanceChanges,
  left,
  right,
}) {
  const summary = [
    summaryFragment(metrics.pathwayDelta, "pathways"),
    summaryFragment(metrics.rootDelta, "independent roots"),
    Number(metrics.convergenceDelta)
      ? `${Number(metrics.convergenceDelta) > 0 ? "higher" : "lower"} convergence (${signed(metrics.convergenceDelta, 3)})`
      : null,
  ].filter(Boolean);

  const leftOrientation = String(left?.orientation || "unresolved").toLowerCase();
  const rightOrientation = String(right?.orientation || "unresolved").toLowerCase();
  const sameOrientation = leftOrientation === rightOrientation;

  return (
    <section className="rr-delta-inspector">
      <div className="rr-delta-section-head">
        <div>
          <span className="v25a-eyebrow">WHAT CHANGED?</span>
          <h4>Configuration B compared with Configuration A</h4>
        </div>
        {metrics.topologyDistance !== null ? (
          <span className="rr-topology-distance">
            topology distance <b>{signed(metrics.topologyDistance, 3).replace("+", "")}</b>
          </span>
        ) : null}
      </div>

      <div className="rr-delta-summary">
        <span className="v25a-eyebrow">PLAIN-LANGUAGE SUMMARY</span>
        <p>
          {summary.length
            ? `Configuration B has ${summary.join(", ")} than Configuration A.`
            : "No non-zero state-level difference is visible in the returned comparison receipt."}
        </p>
        <p>
          Resultant orientation: <b>{leftOrientation.toUpperCase()} → {rightOrientation.toUpperCase()}</b>.
          {sameOrientation
            ? " The orientation is stable; the topology can still differ."
            : " The orientations differ, but this is still not a temporal transition."}
        </p>
      </div>

      <div className="rr-delta-metric-grid">
        <Metric label="Pathways" value={metrics.pathwayDelta} description="B minus A" digits={0} />
        <Metric label="Affording" value={metrics.affordingDelta} description="B minus A" digits={0} />
        <Metric label="Constraining" value={metrics.constrainingDelta} description="B minus A" digits={0} />
        <Metric label="Independent roots" value={metrics.rootDelta} description="B minus A" digits={0} />
        <Metric label="Convergence" value={metrics.convergenceDelta} description="B minus A" digits={3} />
        <Metric label="Divergence" value={metrics.divergenceDelta} description="B minus A" digits={3} />
      </div>

      <div className="rr-root-turnover">
        <span className="v25a-eyebrow">ROOT TOPOLOGY</span>
        <div>
          <b>{metrics.rootTurnover ?? "—"}</b>
          <span>root turnover</span>
        </div>
        <p>
          Root turnover describes which independent StudyRoots participate in
          A versus B. It is not study quality, attrition or time.
        </p>
      </div>

      {rootStanceChanges.length ? (
        <div className="rr-stance-changes">
          <span className="v25a-eyebrow">ROOT STANCE CHANGES</span>
          <div>
            {rootStanceChanges.slice(0, 8).map((change, index) => (
              <span key={change.id || index}>
                <b>{change.rootId || "StudyRoot"}</b>
                <i>↔</i>
                {change.label || change.status || "stance differs"}
              </span>
            ))}
          </div>
        </div>
      ) : null}
    </section>
  );
}
